/**
 * Serviço de Logging de Erros
 * 
 * Centraliza o logging de erros para múltiplos serviços:
 * - Console (desenvolvimento)
 * - Google Analytics (exceções)
 * - Sentry (opcional, se configurado)
 * - LogRocket (opcional, se configurado)
 * 
 * @module utils/errorLogger
 */

import { trackException } from './analytics';

/**
 * Verifica se estamos em ambiente de desenvolvimento
 */
const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Verifica se Sentry está disponível
 */
const isSentryAvailable = () => {
    try {
        return typeof window !== 'undefined' && window.Sentry !== undefined;
    } catch {
        return false;
    }
};

/**
 * Verifica se LogRocket está disponível
 */
const isLogRocketAvailable = () => {
    try {
        return typeof window !== 'undefined' && window.LogRocket !== undefined;
    } catch {
        return false;
    }
};

/**
 * Extrai informações úteis do erro para logging
 * 
 * @param {Error} error - Objeto de erro
 * @param {object} errorInfo - Informações adicionais do erro (opcional)
 * @param {object} context - Contexto adicional (opcional)
 * @returns {object} Informações estruturadas do erro
 */
const extractErrorInfo = (error, errorInfo = null, context = {}) => {
    const errorInfoObj = {
        message: error?.message || 'Erro desconhecido',
        name: error?.name || 'Error',
        stack: error?.stack || null,
        componentStack: errorInfo?.componentStack || null,
        timestamp: new Date().toISOString(),
        url: typeof window !== 'undefined' ? window.location.href : null,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        ...context,
    };

    // Adiciona informações de resposta HTTP se disponível
    if (error?.response) {
        errorInfoObj.httpStatus = error.response.status;
        errorInfoObj.httpStatusText = error.response.statusText;
        errorInfoObj.responseData = error.response.data;
        errorInfoObj.requestUrl = error.config?.url || null;
        errorInfoObj.requestMethod = error.config?.method?.toUpperCase() || null;
    }

    return errorInfoObj;
};

/**
 * Loga erro no console (apenas em desenvolvimento)
 * 
 * @param {object} errorInfo - Informações do erro
 */
const logToConsole = (errorInfo) => {
    if (isDevelopment) {
        console.group('🔴 Erro Capturado');
        console.error('Mensagem:', errorInfo.message);
        console.error('Tipo:', errorInfo.name);
        if (errorInfo.stack) {
            console.error('Stack:', errorInfo.stack);
        }
        if (errorInfo.componentStack) {
            console.error('Component Stack:', errorInfo.componentStack);
        }
        if (errorInfo.httpStatus) {
            console.error('HTTP Status:', errorInfo.httpStatus);
            console.error('Request URL:', errorInfo.requestUrl);
        }
        console.error('Contexto completo:', errorInfo);
        console.groupEnd();
    }
};

/**
 * Loga erro no Sentry (se disponível)
 * 
 * @param {Error} error - Objeto de erro
 * @param {object} errorInfo - Informações adicionais
 * @param {object} context - Contexto adicional
 */
const logToSentry = (error, errorInfo, context) => {
    if (!isSentryAvailable()) {
        return;
    }

    try {
        const sentryContext = {
            tags: {
                component: context.component || 'Unknown',
                section: context.section || 'Unknown',
            },
            extra: {
                ...context,
                componentStack: errorInfo?.componentStack,
            },
        };

        // Se é um erro de componente React
        if (errorInfo?.componentStack) {
            window.Sentry.captureException(error, {
                contexts: {
                    react: {
                        componentStack: errorInfo.componentStack,
                    },
                },
                ...sentryContext,
            });
        } else {
            window.Sentry.captureException(error, sentryContext);
        }
    } catch (err) {
        console.warn('[ErrorLogger] Erro ao enviar para Sentry:', err);
    }
};

/**
 * Loga erro no LogRocket (se disponível)
 * 
 * @param {Error} error - Objeto de erro
 * @param {object} errorInfo - Informações adicionais
 * @param {object} context - Contexto adicional
 */
const logToLogRocket = (error, errorInfo, context) => {
    if (!isLogRocketAvailable()) {
        return;
    }

    try {
        window.LogRocket.captureException(error, {
            tags: {
                component: context.component || 'Unknown',
                section: context.section || 'Unknown',
            },
            extra: {
                ...context,
                componentStack: errorInfo?.componentStack,
            },
        });
    } catch (err) {
        console.warn('[ErrorLogger] Erro ao enviar para LogRocket:', err);
    }
};

/**
 * Loga erro no Google Analytics
 * 
 * @param {object} errorInfo - Informações do erro
 */
const logToAnalytics = (errorInfo) => {
    try {
        const description = errorInfo.httpStatus
            ? `${errorInfo.message} (HTTP ${errorInfo.httpStatus})`
            : errorInfo.message;

        trackException(description, errorInfo.fatal || false);
    } catch (err) {
        console.warn('[ErrorLogger] Erro ao enviar para Analytics:', err);
    }
};

/**
 * Função principal para logar erros
 * 
 * @param {Error} error - Objeto de erro
 * @param {object} options - Opções de logging
 * @param {object} options.errorInfo - Informações adicionais do erro (para Error Boundaries)
 * @param {object} options.context - Contexto adicional (componente, seção, etc.)
 * @param {boolean} options.fatal - Se o erro é fatal (padrão: false)
 * @param {boolean} options.skipConsole - Se deve pular o log no console (padrão: false)
 * @param {boolean} options.skipAnalytics - Se deve pular o log no Analytics (padrão: false)
 * @param {boolean} options.skipSentry - Se deve pular o log no Sentry (padrão: false)
 * @param {boolean} options.skipLogRocket - Se deve pular o log no LogRocket (padrão: false)
 * 
 * @example
 * ```javascript
 * import { logError } from '../utils/errorLogger';
 * 
 * try {
 *     // código que pode falhar
 * } catch (error) {
 *     logError(error, {
 *         context: { component: 'Dashboard', section: 'KPISection' },
 *         fatal: false,
 *     });
 * }
 * ```
 */
export const logError = (error, options = {}) => {
    const {
        errorInfo = null,
        context = {},
        fatal = false,
        skipConsole = false,
        skipAnalytics = false,
        skipSentry = false,
        skipLogRocket = false,
    } = options;

    // Extrai informações do erro
    const errorInfoObj = extractErrorInfo(error, errorInfo, {
        ...context,
        fatal,
    });

    // Loga em cada serviço disponível
    if (!skipConsole) {
        logToConsole(errorInfoObj);
    }

    if (!skipAnalytics) {
        logToAnalytics(errorInfoObj);
    }

    if (!skipSentry) {
        logToSentry(error, errorInfo, { ...context, fatal });
    }

    if (!skipLogRocket) {
        logToLogRocket(error, errorInfo, { ...context, fatal });
    }

    return errorInfoObj;
};

/**
 * Loga erro de componente React (Error Boundary)
 * 
 * @param {Error} error - Objeto de erro
 * @param {object} errorInfo - Informações do componente que lançou o erro
 * @param {object} context - Contexto adicional
 * 
 * @example
 * ```javascript
 * import { logComponentError } from '../utils/errorLogger';
 * 
 * componentDidCatch(error, errorInfo) {
 *     logComponentError(error, errorInfo, {
 *         component: 'DashboardPage',
 *         section: 'KPISection',
 *     });
 * }
 * ```
 */
export const logComponentError = (error, errorInfo, context = {}) => {
    return logError(error, {
        errorInfo,
        context: {
            ...context,
            errorType: 'component',
        },
        fatal: true, // Erros de componente são considerados fatais
    });
};

/**
 * Loga erro de requisição HTTP
 * 
 * @param {Error} error - Objeto de erro do axios
 * @param {object} context - Contexto adicional
 * 
 * @example
 * ```javascript
 * import { logHttpError } from '../utils/errorLogger';
 * 
 * try {
 *     await api.get('/dashboard/summary');
 * } catch (error) {
 *     logHttpError(error, {
 *         component: 'useDashboard',
 *         endpoint: '/dashboard/summary',
 *     });
 * }
 * ```
 */
export const logHttpError = (error, context = {}) => {
    const httpStatus = error?.response?.status;
    
    // Não loga erros 401/403 (autenticação/autorização) como fatais
    const fatal = httpStatus && httpStatus >= 500;

    return logError(error, {
        context: {
            ...context,
            errorType: 'http',
        },
        fatal,
        skipConsole: httpStatus === 401 || httpStatus === 403, // Não loga no console erros de auth
    });
};

/**
 * Inicializa serviços de logging (Sentry, LogRocket, etc.)
 * 
 * Esta função deve ser chamada no início da aplicação (App.js)
 * 
 * @param {object} config - Configuração dos serviços
 * @param {string} config.sentryDsn - DSN do Sentry (opcional)
 * @param {string} config.logRocketAppId - App ID do LogRocket (opcional)
 * @param {object} config.user - Informações do usuário (opcional)
 * 
 * @example
 * ```javascript
 * import { initializeErrorLogging } from './utils/errorLogger';
 * 
 * initializeErrorLogging({
 *     sentryDsn: process.env.REACT_APP_SENTRY_DSN,
 *     logRocketAppId: process.env.REACT_APP_LOGROCKET_APP_ID,
 *     user: { id: '123', email: 'user@example.com' },
 * });
 * ```
 */
export const initializeErrorLogging = (config = {}) => {
    const { sentryDsn, logRocketAppId, user } = config;

    // Inicializa Sentry se DSN fornecido
    if (sentryDsn && typeof window !== 'undefined') {
        try {
            // Nota: Você precisa instalar @sentry/react e importar aqui
            // import * as Sentry from '@sentry/react';
            // Sentry.init({ dsn: sentryDsn, ... });
            console.log('[ErrorLogger] Sentry DSN fornecido, mas Sentry não está instalado.');
        } catch (err) {
            console.warn('[ErrorLogger] Erro ao inicializar Sentry:', err);
        }
    }

    // Inicializa LogRocket se App ID fornecido
    if (logRocketAppId && typeof window !== 'undefined') {
        try {
            // Nota: Você precisa instalar logrocket e importar aqui
            // import LogRocket from 'logrocket';
            // LogRocket.init(logRocketAppId);
            console.log('[ErrorLogger] LogRocket App ID fornecido, mas LogRocket não está instalado.');
        } catch (err) {
            console.warn('[ErrorLogger] Erro ao inicializar LogRocket:', err);
        }
    }

    // Define usuário nos serviços se disponível
    if (user) {
        if (isSentryAvailable() && window.Sentry.setUser) {
            window.Sentry.setUser(user);
        }
        if (isLogRocketAvailable() && window.LogRocket.identify) {
            window.LogRocket.identify(user.id, user);
        }
    }
};

export default {
    logError,
    logComponentError,
    logHttpError,
    initializeErrorLogging,
};

